import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import './ApiKeyField.css';

// An API key that is typed or generated, shown in plain text, and copied from the same row.
//
// Extracted from the sync task's Connector card so the marketplace ESB Integration tab (a proxy whose
// authentication scheme is API_KEY) shows its key the same way rather than growing a second version.
//
// SHOWN IN PLAIN TEXT, NOT AS A PASSWORD. The key is handed to whoever configures the calling system and
// forms part of the URL a property is given, so it has to be readable and copyable here. autoComplete off
// rather than "new-password", so browsers do not offer to save it as a login.
//
// GENERATED WITH crypto.getRandomValues, not Math.random: this is a credential, and Math.random is not a
// source of secrets. Same 40-character alphanumeric shape the sync screen has always produced.
const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const KEY_LENGTH = 40;

export const generateApiKey = () => {
    const bytes = new Uint32Array(KEY_LENGTH);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, b => CHARS[b % CHARS.length]).join('');
};

const ApiKeyField = ({
    id = 'api-key',
    label = 'API Key',
    value,
    onChange,
    required = false,
    hint = 'Stored encrypted and used to authenticate incoming requests.',
    onCopied,
    readOnly = false,
    className = '',
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            if (onCopied) onCopied();
        } catch (e) {
            // The clipboard can refuse (insecure origin, lost user activation). The key is on screen in
            // plain text, so selecting it by hand still works — nothing is lost by not copying.
        }
    };

    return (
        <div className={`form-group api-key-field ${className}`}>
            <label htmlFor={id} className="form-label">
                {label}{required && <span className="required"> *</span>}
            </label>
            <input
                id={id}
                type="text"
                className="form-input api-key-field-input"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                readOnly={readOnly}
                placeholder={readOnly ? '' : 'Enter or generate an API key'}
                autoComplete="off"
                spellCheck={false}
            />
            <div className="api-key-field-actions">
                {!readOnly && (
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => onChange(generateApiKey())}
                        title="Generate API key"
                    >
                        Generate
                    </button>
                )}
                <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCopy}
                    title={copied ? 'Copied' : 'Copy API key'}
                    disabled={!value}
                >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
            </div>
            {hint && <p className="form-hint api-key-field-hint">{hint}</p>}
        </div>
    );
};

export default ApiKeyField;
